import React from 'react';
import { motion } from 'motion/react';
import { Flame, Feather, Sparkles, ShoppingBag } from 'lucide-react';
import { INGREDIENTS, REVIEWS } from '../../data/candles';
import { CandleProduct } from '../../types';
const MobileCandleDeck = React.lazy(() =>
  import('./MobileCandleDeck').then((m) => ({ default: m.MobileCandleDeck }))
);
const FinalCinematicEnding = React.lazy(() =>
  import('../FinalCinematicEnding').then((m) => ({ default: m.FinalCinematicEnding }))
);
import { getMasterSceneAlpha, MOBILE_SCENES } from '../../utils/masterTimeline';
import { IntersectionLazySection } from '../IntersectionLazySection';

interface MobileOverlayProps {
  scrollProgress: number;
  activeCandleIndex: number;
  setActiveCandleIndex: (idx: number) => void;
  onOpenCart: (candle?: CandleProduct) => void;
  onOpenFounderModal: () => void;
  onOpenCustomizerModal: () => void;
  onScrollToProgress: (ratio: number) => void;
  onTriggerShopTransition?: () => void;
}

export const MobileOverlay: React.FC<MobileOverlayProps> = React.memo(({
  scrollProgress,
  activeCandleIndex,
  setActiveCandleIndex,
  onOpenCart,
  onOpenFounderModal,
  onOpenCustomizerModal,
  onScrollToProgress,
  onTriggerShopTransition,
}) => {
  const p = scrollProgress;

  const a1 = getMasterSceneAlpha(p, MOBILE_SCENES[0].start, MOBILE_SCENES[0].end);
  const a2 = getMasterSceneAlpha(p, MOBILE_SCENES[1].start, MOBILE_SCENES[1].end);
  const a3 = getMasterSceneAlpha(p, MOBILE_SCENES[2].start, MOBILE_SCENES[2].end);
  const a4 = getMasterSceneAlpha(p, MOBILE_SCENES[3].start, MOBILE_SCENES[3].end);
  const a5 = getMasterSceneAlpha(p, MOBILE_SCENES[4].start, MOBILE_SCENES[4].end);
  const a6 = getMasterSceneAlpha(p, MOBILE_SCENES[5].start, MOBILE_SCENES[5].end);
  const a7 = getMasterSceneAlpha(p, MOBILE_SCENES[6].start, MOBILE_SCENES[6].end);
  const a8 = getMasterSceneAlpha(p, MOBILE_SCENES[7].start, MOBILE_SCENES[7].end);
  const a9 = getMasterSceneAlpha(p, MOBILE_SCENES[8].start, MOBILE_SCENES[8].end);
  const a10 = getMasterSceneAlpha(p, MOBILE_SCENES[9].start, MOBILE_SCENES[9].end, 0.20);

  return (
    <main className="fixed inset-0 z-10 pointer-events-none flex flex-col justify-center items-center text-center p-5 pt-20 pb-16 text-[#f5f5f0] selection:bg-amber-500/30">
      {/* Natural Centered Circular Candlelight Atmosphere */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] h-[380px] rounded-full opacity-35 bg-[radial-gradient(circle_at_center,_rgba(255,185,75,0.30)_0%,_rgba(200,120,40,0.12)_35%,_rgba(80,45,15,0.04)_65%,_transparent_80%)] pointer-events-none filter blur-xl" />
      {/* Soft Atmospheric Golden Foreground Surface at Bottom */}
      <div className="absolute inset-x-0 bottom-0 h-64 opacity-40 bg-gradient-to-t from-[#3d2b15]/50 via-[#26190b]/15 to-transparent pointer-events-none filter blur-lg" />

      <div className="relative z-20 w-full max-w-sm mx-auto pointer-events-auto flex items-center justify-center">
        {/* SCENE 1: Silence & Intention (0.00 - 0.10) */}
        <IntersectionLazySection scrollProgress={p} startProgress={MOBILE_SCENES[0].start} alpha={a1} isHero={true}>
          <div
            style={{
              opacity: a1,
              transform: `translate3d(0, ${(1 - a1) * 16}px, 0) scale(${0.98 + 0.02 * a1})`,
              willChange: 'transform, opacity',
              pointerEvents: a1 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-4 p-6 rounded-2xl border border-white/10 bg-[#0a0705]/92 shadow-2xl"
          >
            <span className="text-[10px] uppercase tracking-[0.4em] font-sans text-amber-300">
              Silence & Intention
            </span>
            <h1 className="text-2xl font-serif font-light text-[#f5f5f0] leading-snug">
              In complete darkness, light begins with intention.
            </h1>
            <p className="text-xs font-sans text-amber-200/70 uppercase tracking-[0.2em]">
              Scroll down to strike the spark
            </p>
          </div>
        </IntersectionLazySection>

        {/* SCENE 2: Ignition (0.06 - 0.17) */}
        <IntersectionLazySection scrollProgress={p} startProgress={MOBILE_SCENES[1].start} alpha={a2} isHero={true}>
          <div
            style={{
              opacity: a2,
              transform: `translate3d(0, ${(1 - a2) * 16}px, 0) scale(${0.98 + 0.02 * a2})`,
              willChange: 'transform, opacity',
              pointerEvents: a2 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-3 p-6 rounded-2xl border border-amber-400/30 bg-[#0a0705]/92 shadow-2xl"
          >
            <div className="w-10 h-10 rounded-full border border-amber-400/40 bg-amber-500/20 mx-auto flex items-center justify-center">
              <Flame className="w-5 h-5 text-amber-300 animate-pulse" />
            </div>
            <span className="text-[10px] uppercase tracking-[0.4em] font-sans text-amber-300 block">
              The Ignition
            </span>
            <h2 className="text-3xl font-serif font-light text-[#f5f5f0]">
              A single flame transforms room and soul.
            </h2>
          </div>
        </IntersectionLazySection>

        {/* SCENE 3: Hero Title (0.13 - 0.24) */}
        <IntersectionLazySection scrollProgress={p} startProgress={MOBILE_SCENES[2].start} alpha={a3} isHero={true}>
          <div
            style={{
              opacity: a3,
              transform: `translate3d(0, ${(1 - a3) * 16}px, 0) scale(${0.98 + 0.02 * a3})`,
              willChange: 'transform, opacity',
              pointerEvents: a3 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-5 p-6 rounded-3xl border border-white/15 bg-[#0a0705]/94 shadow-2xl"
          >
            <span className="text-[10px] font-sans uppercase tracking-[0.4em] text-amber-300 block">
              Handcrafted Soy Candles
            </span>
            <h1 className="text-6xl font-serif font-extralight text-[#f5f5f0] tracking-[0.15em] uppercase leading-none">
              NOOR PIECES
            </h1>
            <p className="text-base font-light tracking-[0.2em] uppercase text-white/80">
              Light a Moment.
            </p>

            <div className="flex flex-col gap-2.5 pt-2">
              <button
                onClick={() => onScrollToProgress(0.72)}
                className="w-full h-12 bg-white text-black rounded-full text-xs font-sans uppercase tracking-[0.2em] font-semibold active:scale-98 transition-transform shadow-xl"
              >
                Shop Collection
              </button>
              <button
                onClick={onOpenFounderModal}
                className="w-full h-11 border border-white/25 rounded-full text-xs font-sans uppercase tracking-[0.2em] bg-white/5 active:scale-98 transition-transform"
              >
                Explore Rashmi’s Story
              </button>
            </div>
          </div>
        </IntersectionLazySection>

        {/* SCENE 4: Into the Warmth (0.20 - 0.31) */}
        <IntersectionLazySection scrollProgress={p} startProgress={MOBILE_SCENES[3].start} alpha={a4}>
          <div
            style={{
              opacity: a4,
              transform: `translate3d(0, ${(1 - a4) * 16}px, 0) scale(${0.98 + 0.02 * a4})`,
              willChange: 'transform, opacity',
              pointerEvents: a4 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="p-6 rounded-2xl border border-white/10 bg-[#0a0705]/92 shadow-2xl"
          >
            <p className="text-xl font-serif italic text-[#f5f5f0] font-light">
              "Step inside the warmth..."
            </p>
          </div>
        </IntersectionLazySection>

        {/* SCENE 5: Pure Soy Wax Alchemy (0.28 - 0.36) */}
        <IntersectionLazySection scrollProgress={p} startProgress={MOBILE_SCENES[4].start} alpha={a5}>
          <div
            style={{
              opacity: a5,
              transform: `translate3d(0, ${(1 - a5) * 16 - 16}px, 0) scale(${0.98 + 0.02 * a5})`,
              willChange: 'transform, opacity',
              pointerEvents: a5 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-3 p-5 sm:p-6 rounded-2xl border border-[#C8A45A]/30 bg-[#0a0705]/50 backdrop-blur-md shadow-[0_0_30px_rgba(0,0,0,0.5)] text-left"
          >
            <span className="text-[10px] font-sans uppercase tracking-[0.3em] text-amber-300 block font-semibold">
              Pure Soy Wax Alchemy
            </span>
            <h3 className="text-xl font-serif text-[#f5f5f0] font-light leading-snug">
              Golden botanical oils dancing in molten liquid warmth.
            </h3>
            <p className="text-xs font-sans text-[#f5f5f0]/80 leading-relaxed font-light">
              100% renewable soy & coconut wax infused with therapeutic botanical essences. Zero parabens, zero phthalates.
            </p>
          </div>
        </IntersectionLazySection>

        {/* SCENE 6: Rashmi's Workshop (0.36 - 0.50) */}
        <IntersectionLazySection scrollProgress={p} startProgress={MOBILE_SCENES[5].start} alpha={a6}>
          <div
            style={{
              opacity: a6,
              transform: `translate3d(0, ${(1 - a6) * 16}px, 0) scale(${0.98 + 0.02 * a6})`,
              willChange: 'transform, opacity',
              pointerEvents: a6 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-4 p-6 rounded-3xl border border-amber-400/30 bg-black/85 backdrop-blur-xl text-center"
          >
            <div className="w-10 h-10 rounded-full border border-amber-300/30 mx-auto flex items-center justify-center bg-amber-500/10">
              <Feather className="w-4 h-4 text-amber-300" />
            </div>
            <p className="text-xl font-serif text-[#f5f5f0] italic font-light">
              "Every candle begins with patient hands."
            </p>
            <p className="text-[10px] font-sans text-amber-300 uppercase tracking-[0.2em]">
              Rashmi • Master Chandler
            </p>
            <button
              onClick={onOpenFounderModal}
              className="w-full h-10 rounded-full border border-white/20 bg-white/5 text-xs font-sans uppercase tracking-widest text-amber-200 active:scale-98 transition-transform"
            >
              Read Artisanal Philosophy
            </button>
          </div>
        </IntersectionLazySection>

        {/* SCENE 7: Ingredients Grid (0.46 - 0.60) */}
        <IntersectionLazySection scrollProgress={p} startProgress={MOBILE_SCENES[6].start} alpha={a7}>
          <div
            style={{
              opacity: a7,
              transform: `translate3d(0, ${(1 - a7) * 16}px, 0) scale(${0.98 + 0.02 * a7})`,
              willChange: 'transform, opacity',
              pointerEvents: a7 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-3 w-full text-left"
          >
            <span className="text-[10px] font-sans uppercase tracking-[0.3em] text-amber-300 block text-center">
              Botanical Ingredients
            </span>
            <div className="grid grid-cols-2 gap-2">
              {INGREDIENTS.slice(0, 4).map((ing, i) => (
                <div key={i} className="p-3 border border-white/10 bg-black/70 backdrop-blur-md rounded-xl">
                  <p className="text-xs font-serif text-amber-200 font-medium">{ing.name}</p>
                  <p className="text-[9px] font-sans text-white/50 uppercase tracking-widest mt-1">{ing.origin}</p>
                </div>
              ))}
            </div>
          </div>
        </IntersectionLazySection>

        {/* SCENE 8: Masterpiece Collection Deck (0.56 - 0.84) */}
        <IntersectionLazySection scrollProgress={p} startProgress={MOBILE_SCENES[7].start} alpha={a8}>
          <div
            style={{
              opacity: a8,
              willChange: 'opacity',
              pointerEvents: a8 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="w-full"
          >
            <React.Suspense fallback={null}>
              <MobileCandleDeck
                activeCandleIndex={activeCandleIndex}
                setActiveCandleIndex={setActiveCandleIndex}
                onOpenCart={onOpenCart}
              />
            </React.Suspense>
          </div>
        </IntersectionLazySection>

        {/* SCENE 9: Customer Stories (0.80 - 0.94) */}
        <IntersectionLazySection scrollProgress={p} startProgress={MOBILE_SCENES[8].start} alpha={a9}>
          <div
            style={{
              opacity: a9,
              transform: `translate3d(0, ${(1 - a9) * 16}px, 0) scale(${0.98 + 0.02 * a9})`,
              willChange: 'transform, opacity',
              pointerEvents: a9 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="space-y-3 text-left w-full"
          >
            <span className="text-[10px] font-sans uppercase tracking-[0.3em] text-amber-300 block text-center">
              Illuminated Stories
            </span>
            {REVIEWS.slice(0, 2).map((rev) => (
              <div key={rev.id} className="p-4 border border-white/15 bg-black/80 backdrop-blur-xl rounded-2xl space-y-2">
                <p className="font-serif italic text-white/90 text-xs leading-relaxed">
                  "{rev.quote}"
                </p>
                <div className="flex justify-between items-center text-[9px] font-sans text-amber-300/80 uppercase tracking-widest pt-2 border-t border-white/10">
                  <span>— {rev.author}</span>
                  <span>{rev.candleName}</span>
                </div>
              </div>
            ))}
          </div>
        </IntersectionLazySection>

        {/* SCENE 10: Emotional Final Ending (0.90 - 1.00) */}
        <IntersectionLazySection scrollProgress={p} startProgress={MOBILE_SCENES[9].start} alpha={a10}>
          <div
            style={{
              opacity: a10,
              willChange: 'opacity',
              pointerEvents: a10 > 0.5 ? 'auto' : 'none',
              position: 'absolute',
            }}
            className="w-full"
          >
            <React.Suspense fallback={null}>
              <FinalCinematicEnding
                onTriggerShopTransition={onTriggerShopTransition}
                onOpenCart={onOpenCart}
              />
            </React.Suspense>
          </div>
        </IntersectionLazySection>
      </div>
    </main>
  );
});
